import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import apiFetch from "../../utlis/apiFetch"
import { forceLogout } from "../../utlis/forceLogout"

const handleAuthError = (error, rejectWithValue) => {
  if (error.message === "Your session has expired") forceLogout()
  return rejectWithValue(error.message)
}

// Initial state
const initialState = {
  bankDetails: [], // Store all bank details
  loading: false,
  error: null,
  success: false,
}

// Fetch all bank details
export const fetchBankDetails = createAsyncThunk("bankDetails/fetchAll", async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/api/bankdetails/getAllBankDetails`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    const data = response._data ?? await response.json();
    if (!response.ok) throw new Error(data.message || "Failed to fetch bank details")

    return data
  } catch (error) {
    return handleAuthError(error, rejectWithValue)
  }
})

// Create bank details
export const createBankDetails = createAsyncThunk(
  "bankDetails/create",
  async (bankDetailsData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token")
      const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/api/bankdetails/createBankDetails`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(bankDetailsData),
      })

      const data = response._data ?? await response.json()
      if (!response.ok) throw new Error(data.message || "Failed to create bank details")

      return data
    } catch (error) {
      console.error("Error creating bank details:", error)
      return handleAuthError(error, rejectWithValue)
    }
  },
)

// Update bank details
export const updateBankDetails = createAsyncThunk(
  "bankDetails/update",
  async (bankDetailsData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token")
      const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/api/bankdetails/updateBankDetails`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(bankDetailsData),
      })

      const data = response._data ?? await response.json()
      if (!response.ok) throw new Error(data.message || "Failed to update bank details")

      return data
    } catch (error) {
      return handleAuthError(error, rejectWithValue)
    }
  },
)

// Bank Details Slice
const bankDetailsSlice = createSlice({
  name: "bankDetails",
  initialState,
  reducers: {
    // Reset success state if needed
    resetSuccess: (state) => {
      state.success = false
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetching bank details
      .addCase(fetchBankDetails.pending, (state) => {
        state.loading = true
        state.success = false
      })
      .addCase(fetchBankDetails.fulfilled, (state, action) => {
        state.loading = false
        state.bankDetails = action.payload.body
        state.error = null
      })
      .addCase(fetchBankDetails.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      // Handle creating bank details
      .addCase(createBankDetails.pending, (state) => {
        state.loading = true
        state.success = false
        state.error = null
      })
      .addCase(createBankDetails.fulfilled, (state, action) => {
        state.loading = false
        state.bankDetails.push(action.payload.body) // Add new bank details to state
        state.error = null
        state.success = true // Set success to true for animation
        console.log("Bank details created successfully")
      })
      .addCase(createBankDetails.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      // Handle updating bank details
      .addCase(updateBankDetails.pending, (state) => {
        state.loading = true
        state.success = false
        state.error = null
      })
      .addCase(updateBankDetails.fulfilled, (state, action) => {
        state.loading = false
        const updatedBankDetails = action.payload.body
        state.bankDetails = state.bankDetails.map((details) => {
          if (details.country === updatedBankDetails.country) {
            return updatedBankDetails // Replace with updated data
          }
          return details // Keep unchanged bank details
        })
        state.error = null
        state.success = true // Set success to true for animation
        console.log("Bank details updated successfully")
      })
      .addCase(updateBankDetails.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })
  },
})

// Selector to get bank details by country
export const selectBankDetailsByCountry = (state, country) => {
  return state.bankDetails.bankDetails.find((details) => details.country === country)
}

export const { resetSuccess } = bankDetailsSlice.actions
export default bankDetailsSlice.reducer

